import { fetchJson, postJson } from "@/api/client";
import type { PortalAdapter } from "@/api/portal-adapter";
import type { ClientEvent, EventBatch } from "@/lib/tracker";
import type {
  ClassReportResponse,
  DistrictReportResponse,
  PortalHomeResponse,
  PortalNavigationResponse,
  PortalPopulationReferenceResponse,
  PortalSessionResponse,
  SchoolReportResponse,
  StudentReportResponse,
} from "@/contracts/portal";
import {
  validateNavigationResponse,
  validatePopulationReferenceResponse,
  validatePortalHomeResponse,
  validateSessionResponse,
} from "@/api/validate";

function findDistrict(home: PortalHomeResponse, districtId: string) {
  return home.districts.find((district) => district.id === districtId) ?? null;
}

function findSchool(home: PortalHomeResponse, districtId: string, schoolId: string) {
  const district = findDistrict(home, districtId);
  const school = district?.schools.find((item) => item.id === schoolId) ?? null;
  return { district, school };
}

function findClass(home: PortalHomeResponse, districtId: string, schoolId: string, classId: string) {
  const { district, school } = findSchool(home, districtId, schoolId);
  const klass = school?.classes.find((item) => item.id === classId) ?? null;
  return { district, school, klass };
}

function findStudent(home: PortalHomeResponse, districtId: string, schoolId: string, classId: string, studentId: string) {
  const { district, school, klass } = findClass(home, districtId, schoolId, classId);
  const student = klass?.students.find((item) => item.id === studentId) ?? null;
  return { district, school, klass, student };
}

function normaliseDataBaseUrl(dataBaseUrl: string): string {
  const trimmed = dataBaseUrl.trim().replace(/\/+$/, "");
  return trimmed || "app-data";
}

export function createStaticJsonPortalAdapter(dataBaseUrl: string): PortalAdapter {
  let cachedHome: PortalHomeResponse | null = null;
  let cachedPopulationReference: PortalPopulationReferenceResponse | null | undefined;

  const baseUrl = normaliseDataBaseUrl(dataBaseUrl);
  const cacheBust = `v=${Date.now()}`;
  const dataUrl = (fileName: string) => `${baseUrl}/${fileName}?${cacheBust}`;

  async function home(): Promise<PortalHomeResponse> {
    if (!cachedHome) {
      cachedHome = validatePortalHomeResponse(
        await fetchJson<PortalHomeResponse>(dataUrl("portal-home.json"), { cache: "no-store" }),
      );
    }
    return cachedHome;
  }

  async function populationReference(): Promise<PortalPopulationReferenceResponse | null> {
    if (cachedPopulationReference !== undefined) return cachedPopulationReference;
    try {
      cachedPopulationReference = validatePopulationReferenceResponse(
        await fetchJson<PortalPopulationReferenceResponse>(
          dataUrl("population-reference.json"),
          { cache: "no-store" },
        ),
      );
    } catch {
      cachedPopulationReference = null;
    }
    return cachedPopulationReference;
  }

  return {
    async postEvents(events: ClientEvent[], init?: RequestInit) {
      const payload: EventBatch = { events };
      try {
        await postJson(`${baseUrl}/events`, payload, init);
      } catch {
        // Static handover packs have no event collector. Ignore by design.
      }
    },

    async getSession() {
      return validateSessionResponse(
        await fetchJson<PortalSessionResponse>(dataUrl("session.json"), { cache: "no-store" }),
      );
    },

    async getNavigation() {
      return validateNavigationResponse(
        await fetchJson<PortalNavigationResponse>(dataUrl("navigation.json"), { cache: "no-store" }),
      );
    },

    getPortalHome() {
      return home();
    },

    getPopulationReference() {
      return populationReference();
    },

    async getDistrictReport(districtId: string): Promise<DistrictReportResponse | null> {
      const h = await home();
      const district = findDistrict(h, districtId);
      return district ? { release: h.release, district, districtTermSummaries: h.districtTermSummaries } : null;
    },

    async getSchoolReport(districtId: string, schoolId: string): Promise<SchoolReportResponse | null> {
      const h = await home();
      const { district, school } = findSchool(h, districtId, schoolId);
      return district && school ? { release: h.release, district, school } : null;
    },

    async getClassReport(districtId: string, schoolId: string, classId: string): Promise<ClassReportResponse | null> {
      const h = await home();
      const { district, school, klass } = findClass(h, districtId, schoolId, classId);
      return district && school && klass ? { release: h.release, district, school, klass } : null;
    },

    async getStudentReport(districtId: string, schoolId: string, classId: string, studentId: string): Promise<StudentReportResponse | null> {
      const h = await home();
      const { district, school, klass, student } = findStudent(h, districtId, schoolId, classId, studentId);
      return district && school && klass && student ? { release: h.release, district, school, klass, student } : null;
    },
  };
}
